import { Form } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { Store } from 'antd/lib/form/interface'
import { useEffect, useState } from 'react'
import FormlessModal from './FormlessModal'
import { CIMSModalProps } from './types'

/**
 * A Wrapper around Ant Design's Modal component meant to standardize modals across
 * the application and expose only necessary props.
 *
 * Use this in conjunction with
 * - {@link CIMSModalWrapperProps}
 * - {@link useCIMSModal}
 *
 * Not all modal based components have moved to this modal, but all components
 * using Ant Design modals should be able to easily switch over
 *
 * @see {@link FormlessModal}
 */
export function CIMSModal<T = any>({
  initialFormValues,
  onCancel,
  onOk,
  children,
  loading,
  disabled,
  externalForm,
  visible,
  ...rest
}: CIMSModalProps<T>) {
  const [_loading, setLoading] = useState(loading)
  const ok = async () => {
    if (onOk === undefined) return
    try {
      const results = await form.validateFields()
      await onOk(results)
    } catch (err) {
      // ignore errors, the component will display them
      return
    }
  }

  useEffect(() => {
    setLoading(loading)
    return () => setLoading(false)
  }, [loading])

  const [internalForm] = useForm()
  const form = externalForm ?? internalForm

  useEffect(() => {
    if (visible && initialFormValues) form.setFieldsValue(initialFormValues)
    // Don't make initialFormValues reactive, as it often points to a new
    // reference each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, form])

  return (
    <FormlessModal
      {...rest}
      /**
       * form.resetFields only affects the form if you set initial values!
       */
      visible={visible}
      afterClose={() => form.resetFields()}
      ok={{ fn: ok, loading: _loading, props: { disabled } }}
      cancel={{ fn: onCancel }}
    >
      <Form
        /**
         * we kind of have to cast this as {@link Store} here. Otherwise all the onOk
         * props will fail. we've got so comfortable being able to set the type in
         * those callbacks that forcing a constraint would be more work than it's worth
         */
        initialValues={initialFormValues as Store}
        form={form}
      >
        {children}
      </Form>
    </FormlessModal>
  )
}
